import { set } from 'lodash/fp';
import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../global';
import {
  selectBuyAuditorPersonFactory,
  selectBuyCeoPersonFactory,
  selectBuyClientInfoPersonFactory,
  selectBuyLegalShareholdersFactory,
  selectBuyPhysicalPeopleFactory,
  selectBuyPowerOfAttorneyPersonFactory,
  selectBuyRepresentativesPeopleFactory,
} from '../selectors';
import { AuditorOptionsType } from '../../../components/buy-process-form/components/auditor/auditor-types';
import { CUSTOMER_FORM_ID } from '../../../global/constants';
import { SignAuthority } from '../../../global/entities/BuyOrder';
import { SellUtils } from '../../../utils/sell-process';

const selectUpdateClientInfoFactory = (buyOrderId: string) =>
  createSelector(
    (state: RootState) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { emailRepeat, ...clientInfo } = selectBuyClientInfoPersonFactory(buyOrderId)(state);
      return clientInfo;
    },
    (a) => a,
  );

const selectUpdateCompanyDetailsFactory = (buyOrderId: string) =>
  createSelector(
    (state: RootState) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { orderId, templateSelect, ...companyDetails } = state.buyOrderEdit[buyOrderId].companyDetails;
      return {
        ...companyDetails,
        municipality: companyDetails.municipality.id,
      };
    },
    (a) => a,
  );

const selectUpdateOwnerSharesFactory = (buyOrderId: string) =>
  createSelector(
    (state: RootState) => {
      const physicalPeople = selectBuyPhysicalPeopleFactory(buyOrderId)(state);
      const legalShareholders = selectBuyLegalShareholdersFactory(buyOrderId)(state);

      return {
        physicalPeople,
        legalShareholders,
      };
    },
    (a) => a,
  );

const selectUpdateRepresentativesFactory = (buyOrderId: string) =>
  createSelector(
    (state: RootState) => {
      const representatives = selectBuyRepresentativesPeopleFactory(buyOrderId)(state);
      const ceo = selectBuyCeoPersonFactory(buyOrderId)(state);

      return {
        representatives,
        ceo,
        officialCeo: state.buyOrderEdit[buyOrderId].representatives.officialCeo,
      };
    },
    (a) => a,
  );

const selectUpdateAuditorFactory = (buyOrderId: string) =>
  createSelector(
    (state: RootState) => {
      const { id, auditorOption, auditorFirm } = state.buyOrderEdit[buyOrderId].auditor;
      const auditorPerson = selectBuyAuditorPersonFactory(buyOrderId)(state);

      const isDeletingAuditor = (
        auditorOption: AuditorOptionsType,
        ssn?: string,
      ) => auditorOption === 'no' && ssn != null;

      if (
        isDeletingAuditor(auditorOption, auditorPerson?.socialSecurityNumber)
      ) {
        return {
          ...auditorPerson,
          ...auditorFirm,
          type: 'delete',
          id,
        };
      }

      if (auditorOption === 'no' && !auditorPerson?.socialSecurityNumber) {
        return undefined;
      }

      if (auditorOption === 'person') {
        return {
          ...auditorPerson,
          organizationNumber: null,
          organizationName: null,
          organizationAddress: null,
          organizationPostalCode: null,
          organizationCity: null,
          id,
        };
      }

      return {
        ...auditorFirm,
        ...auditorPerson,
        id,
      };
    },
    (a) => a,
  );

const selectUpdatePowerOfAttorneyFactory = (buyOrderId: string) =>
  createSelector(
    (state: RootState) => {
      const powerOfAttorney = selectBuyPowerOfAttorneyPersonFactory(buyOrderId)(state);

      return {
        socialSecurityNumber: powerOfAttorney.socialSecurityNumber,
        firstName: powerOfAttorney.firstName,
        lastName: powerOfAttorney.lastName,
        relationId: powerOfAttorney.relationId,
        personId: powerOfAttorney.personId,
      };
    },
    (a) => a,
  );

const selectUpdateOrderMetadataFactory = (buyOrderId: string) =>
  createSelector(
    (state: RootState) => {
      const buyOrder = state.buyOrderEdit[buyOrderId];

      const { signAuthorityUserDefined, signAuthority } = buyOrder.representatives;
      const { numberShares, beneficialOwnerHelp, shareCapital } = buyOrder.ownerShares;
      const { rightFirstRefusal, stockRestrictions } = buyOrder.reservation;
      const { fiscalYear, extendedFiscalYear, firstClosingDate } = buyOrder.fiscalYear;
      const { costStatement, metadata } = buyOrder;

      const staffMetadataFields = {
        registrationDate: metadata.registrationDate,
        internalDealDate: metadata.internalDealDate,
        invoiceNumber: metadata.invoiceNumber,
        organizationNumber: metadata.organizationNumber,
        organizationName: metadata.organizationName,
        companyNumber:
          metadata.organizationName
          && SellUtils.parseCompanyNumber(metadata.organizationName),
        internalManager: {
          userId: metadata.internalManager?.userId,
          id: metadata.internalManager?.id,
        },
      };

      return {
        ...(buyOrderId !== CUSTOMER_FORM_ID && staffMetadataFields),
        beneficialOwnerHelp,
        shareCapital,
        numberShares,
        rightFirstRefusal,
        stockRestrictions,
        signAuthority:
          signAuthority === SignAuthority.ABX
            ? signAuthorityUserDefined
            : signAuthority,
        fiscalYear: fiscalYear || ('0112' as any),
        firstClosingDate,
        extendedFiscalYear,
        digitalCustomerSignature: costStatement.receiveOption === 'email',
        invoiceRecipient: Number(buyOrder.costStatement.invoiceRecipient),
        manualHandling: costStatement.manualHandling,
        taxAccountOcr: costStatement.taxAccountOcr,
        bankAccountHelp: buyOrder.proxies.bankAccountHelp,
        emails:
          costStatement.receiverEmails
          && JSON.stringify(costStatement.receiverEmails),
      };
    },
    (a) => a,
  );
export const selectBuyUpdateRequestFactory = (buyOrderId: string) =>
  createSelector(
    (state: RootState) => state.buyOrderEdit[buyOrderId],
    (state: RootState) => selectUpdateClientInfoFactory(buyOrderId)(state),
    (state: RootState) => selectUpdateCompanyDetailsFactory(buyOrderId)(state),
    (state: RootState) => selectUpdateOwnerSharesFactory(buyOrderId)(state),
    (state: RootState) => selectUpdateRepresentativesFactory(buyOrderId)(state),
    (state: RootState) => selectUpdateAuditorFactory(buyOrderId)(state),
    (state: RootState) => selectUpdatePowerOfAttorneyFactory(buyOrderId)(state),
    (state: RootState) => selectUpdateOrderMetadataFactory(buyOrderId)(state),
    (
      buyOrder,
      clientInfo,
      companyDetails,
      { physicalPeople, legalShareholders },
      { representatives, ceo, officialCeo },
      auditor,
      powerOfAttorney,
      metadata,
    ) => {
      const result = {
        id: buyOrderId,
        ...metadata,
        officialCeo,
        ceo,
        company: companyDetails,
        client: clientInfo,
        physicalPeople,
        legalShareholders,
        powerOfAttorney,
        representatives,
        auditor,
      };

      if (buyOrderId === CUSTOMER_FORM_ID) {
        // @ts-ignore
        delete result.id;
      }

      // Need to set it to null so that backend can know to delete it
      if (!buyOrder.clientInfo.organizationName) {
        return set('client.organizationName', null, result);
      }

      return result;
    },
  );
